/*
import React, { useRef, useEffect, useState } from "react";

export default function ImageToParticle({ path, width = 800, height = 800, particleSize = 2, numParticles = null }) {
  const canvasAsRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false); // State to manage pause on hover
  const mouseRadius = (width + height) / 6; // Increased mouse radius for stronger effect
  const maxDistanceSquared = mouseRadius * mouseRadius;
  const particleSizeSquared = particleSize * particleSize;
  let hasLoaded = false;

  const positionGrid = [];
  const positionGridRows = Math.ceil(height / mouseRadius);
  const positionGridCols = Math.ceil(width / mouseRadius);

  useEffect(() => {
    class GridCell {
      constructor() {
        this.particles = new Set();
      }

      addParticle(particle) {
        this.particles.add(particle);
      }

      removeParticle(particle) {
        this.particles.delete(particle);
      }
    }

    for (let i = 0; i < positionGridRows; i++) {
      positionGrid[i] = [];
      for (let j = 0; j < positionGridCols; j++) {
        positionGrid[i][j] = new GridCell();
      }
    }

    const canvas = canvasAsRef.current;
    const ctx = canvas.getContext("2d");
    const IS_NUM_PARTICLES_SET = numParticles !== null;
    let NUM_PARTICLES = numParticles !== null ? numParticles : 1000;

    const imageOffsetX = 0.2;
    const imageOffsetY = 0.2;

    canvas.width = width;
    canvas.height = height;
    let particleArr = [];
    let mouseMoved = false;

    let mouse = {
      x: null,
      y: null,
      radius: mouseRadius,
    };

    window.addEventListener('mousemove', function(event) {
      const rect = canvas.getBoundingClientRect();
      mouse.x = event.clientX - rect.left;
      mouse.y = event.clientY - rect.top;
      mouseMoved = true;
    });

    canvas.addEventListener('mouseenter', () => setIsPaused(true)); // Pause animation on hover
    canvas.addEventListener('mouseleave', () => setIsPaused(false)); // Resume animation on leave

    function drawImage(data) {
      class Particle {
        constructor(x, y, color, size) {
          this.x = x;
          this.y = y;
          this.color = color;
          this.originalColor = color; // Store the original color
          this.size = size;
          this.baseX = this.x;
          this.baseY = this.y;
          this.density = (Math.random() * 30) + 4;
          this.positionGridRow = Math.floor(this.y / mouseRadius);
          this.positionGridCol = Math.floor(this.x / mouseRadius);
        }

        draw() {
          ctx.fillStyle = this.color;
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.closePath();
          ctx.fill();
        }

        calculateGridPosition() {
          const row = Math.floor(this.y / mouseRadius);
          const col = Math.floor(this.x / mouseRadius);

          if (row !== this.positionGridRow || col !== this.positionGridCol) {
            if (row >= 0 && row < positionGridRows && col >= 0 && col < positionGridCols) {
              if (this.positionGridCol !== -1 && this.positionGridRow !== -1) {
                positionGrid[this.positionGridRow][this.positionGridCol].removeParticle(this);
              }

              positionGrid[row][col].addParticle(this);
              this.positionGridRow = row;
              this.positionGridCol = col;
            } else {
              this.positionGridRow = -1;
              this.positionGridCol = -1;
            }
          }
        }

        update() {
          if (isPaused) return; // Stop updating position if paused
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          const distanceSquared = Math.abs(dx * dx + dy * dy);

          if (mouseMoved && distanceSquared < maxDistanceSquared + particleSizeSquared) {
            const forceDirectionX = dx / mouseRadius;
            const forceDirectionY = dy / mouseRadius;
            const force = 1 - (distanceSquared / maxDistanceSquared);

            const directionX = forceDirectionX * force * this.density * 2; // Increase force for more spread
            const directionY = forceDirectionY * force * this.density * 2; // Increase force for more spread

            this.x -= directionX;
            this.y -= directionY;

            this.color = "rgba(255,0,0,1)"; // Change color on hover
            this.size = particleSize * 1.5; // Increase size on hover

            this.calculateGridPosition();
          } else {
            this.color = this.originalColor; // Reset color
            this.size = particleSize; // Reset size
          }
        }

        applyForceBackToOriginalPosition() {
          if (!isPaused) {
            this.x -= (this.x - this.baseX) / 10; // Increase speed of returning to base position
            this.y -= (this.y - this.baseY) / 10 + 0.5; // Add slight gravity effect
            this.calculateGridPosition();
          }
        }
      }

      function init() {
        particleArr = [];
        const numPixelsWithPositiveAlpha = data.data.filter((_, i) => i % 4 === 3 && data.data[i] > 128).length;
        NUM_PARTICLES = Math.min(NUM_PARTICLES, numPixelsWithPositiveAlpha);

        for (let y = 0, y2 = data.height; y < y2; y++) {
          for (let x = 0, x2 = data.width; x < x2; x++) {
            if (data.data[(y * 4 * data.width) + (x * 4) + 3] > 128) {
              if (IS_NUM_PARTICLES_SET && NUM_PARTICLES < numPixelsWithPositiveAlpha && Math.random() > NUM_PARTICLES / numPixelsWithPositiveAlpha) {
                continue;
              }

              const positionX = (canvas.width * (imageOffsetX / 2)) + Math.floor((x / data.width) * canvas.width) * (1 - imageOffsetX);
              const positionY = (canvas.height * (imageOffsetY / 2)) + Math.floor((y / data.height) * canvas.height) * (1 - imageOffsetY);
              const index = (y * 4 * data.width) + (x * 4);

              const color = "rgb(" + data.data[index] + "," + data.data[index + 1] + "," + data.data[index + 2] + ")";
              const randomSize = particleSize * (Math.random() * 0.5 + 0.75); // Randomize particle size
              particleArr.push(new Particle(positionX, positionY, color, randomSize));

              const row = Math.floor(positionY / mouseRadius);
              const col = Math.floor(positionX / mouseRadius);

              positionGrid[row][col].particles.add(particleArr[particleArr.length - 1]);
            }
          }
        }
      }

      function animate() {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!isPaused) {
          const mouseRow = Math.floor(mouse.y / mouseRadius);
          const mouseCol = Math.floor(mouse.x / mouseRadius);
          const rowColOffsets = [[0, 1], [0, -1], [1, 1], [1, -1], [1, 0], [-1, 0], [0, 0], [-1, -1], [-1, 1]];

          if (mouseMoved) {
            for (let i = 0; i < rowColOffsets.length; ++i) {
              const particleRow = mouseRow + rowColOffsets[i][0];
              const particleCol = mouseCol + rowColOffsets[i][1];

              if (particleRow < 0 || particleRow >= positionGridRows || particleCol < 0 || particleCol >= positionGridCols)
                continue;

              for (const particle of positionGrid[particleRow][particleCol].particles) {
                particle.update();
              }
            }
          }

          for (let i = 0; i < particleArr.length; i++) {
            if (particleArr[i].x !== particleArr[i].baseX || particleArr[i].y !== particleArr[i].baseY) {
              particleArr[i].applyForceBackToOriginalPosition();
            }

            particleArr[i].draw();
          }

          mouseMoved = false;
        } else {
          // When paused, only draw the particles at their current position
          for (let i = 0; i < particleArr.length; i++) {
            particleArr[i].draw();
          }
        }

        requestAnimationFrame(animate);
      }

      init();
      animate();
    }

    function readImageData(bitmap) {
      const { width: w, height: h } = bitmap;
      const _canvas = new OffscreenCanvas(w, h);
      const _ctx = _canvas.getContext('2d');

      _ctx.drawImage(bitmap, 0, 0);
      const imageData = _ctx.getImageData(0, 0, w, h);

      return imageData;
    }

    window.addEventListener('load', function() {
      if (hasLoaded) {
        return;
      }

      fetch(path)
        .then((r) => r.blob())
        .then(createImageBitmap)
        .then(readImageData)
        .then((pixels) => {
          drawImage(pixels);
        });

      hasLoaded = true;
    });
  }, [isPaused]);

  return <canvas ref={canvasAsRef} />;
}
*/

/* =========================
   PARTICLE IMAGE ENGINE
   (EXACT COPY FROM logo.html)
========================= */

const ParticleImageDisplayer = function (tag_id, canvas_el, params) {
  "use strict";

  this.pImageConfig = {
    particles: {
      array: [],
      density: 300,
      color: ["#800000", "#000080", "green", "yellow", "gray", "#000080", "red", "red"],
      size: { value: 1, random: false },
      movement: {
        speed: 15,
        restless: { enabled: false, value: 20 }
      },
      interactivity: {
        on_hover: { enabled: true, action: "repulse" },
        on_click: { enabled: false, action: "big_repulse" },
        on_touch: { enabled: true, action: "repulse" },
        fn_array: []
      }
    },
    image: {
      src: { path: null, is_external: false },
      size: {
        canvas_pct: 80,
        min_px: 1050,
        max_px: 900,
        responsive: true,
        aspectRatio: 1
      }
    },
    interactions: {
      repulse: { distance: 100, strength: 200 },
      big_repulse: { distance: 100, strength: 500 },
      grab: { distance: 100, line_width: 1 }
    },
    canvas: {
      el: canvas_el,
      w: canvas_el.offsetWidth,
      h: canvas_el.offsetHeight,
      responsive: true,
      aspectRatio: 16 / 9
    },
    functions: { particles: {}, image: {}, canvas: {}, interactivity: {}, utils: {} },
    mouse: { x: null, y: null, click_x: null, click_y: null }
  };

  const pImg = this.pImageConfig;
  if (params) Object.deepExtend(pImg, params);

  /* ===== CANVAS ===== */
  pImg.functions.canvas.init = function () {
    pImg.canvas.context = pImg.canvas.el.getContext("2d");
    pImg.canvas.el.width = pImg.canvas.w;
    pImg.canvas.el.height = pImg.canvas.h;
    window.addEventListener("resize", pImg.functions.utils.debounce(pImg.functions.canvas.onResize, 200));
  };

  pImg.functions.canvas.onResize = function () {
    pImg.canvas.w = pImg.canvas.el.offsetWidth;
    pImg.canvas.h = pImg.canvas.el.offsetHeight;
    pImg.canvas.el.width = pImg.canvas.w;
    pImg.canvas.el.height = pImg.canvas.h;
    pImg.particles.array = [];
    pImg.functions.image.resize();
    const image_pixels = pImg.functions.canvas.getImagePixels();
    pImg.functions.particles.createImageParticles(image_pixels, true);
  };

  pImg.functions.canvas.clear = function () {
    pImg.canvas.context.clearRect(0, 0, pImg.canvas.w, pImg.canvas.h);
  };

  pImg.functions.canvas.getImagePixels = function () {
    pImg.functions.canvas.clear();
    pImg.canvas.context.drawImage(
      pImg.image.obj,
      pImg.image.x,
      pImg.image.y,
      pImg.image.obj.width,
      pImg.image.obj.height
    );
    const pixel_data = pImg.canvas.context.getImageData(
      pImg.image.x,
      pImg.image.y,
      pImg.image.obj.width,
      pImg.image.obj.height
    );
    pImg.functions.canvas.clear();
    return pixel_data;
  };

  /* ===== IMAGE ===== */
  pImg.functions.image.resize = function () {
    pImg.image.obj.width = Math.round(pImg.canvas.w * 0.8);
    pImg.image.obj.height = Math.round(pImg.image.obj.width / pImg.image.aspect_ratio);
    pImg.image.x = pImg.canvas.w / 2 - pImg.image.obj.width / 2;
    pImg.image.y = pImg.canvas.h / 2 - pImg.image.obj.height / 2;
  };

  pImg.functions.image.init = function () {
    pImg.image.obj = new Image();
    pImg.image.obj.onload = function () {
      pImg.image.aspect_ratio = pImg.image.obj.width / pImg.image.obj.height;
      pImg.functions.image.resize();
      const pixels = pImg.functions.canvas.getImagePixels();
      pImg.functions.particles.createImageParticles(pixels);
      pImg.functions.particles.animateParticles();
    };
    pImg.image.obj.src = pImg.image.src.path;
  };

  /* ===== PARTICLES ===== */
  pImg.functions.particles.SingleImageParticle = function (init, dest) {
    this.x = init.x;
    this.y = init.y;
    this.dest_x = dest.x;
    this.dest_y = dest.y;
    this.vx = (Math.random() - 0.5) * 15;
    this.vy = (Math.random() - 0.5) * 15;
    this.friction = 0.92;
    this.color = pImg.particles.color[Math.floor(Math.random() * pImg.particles.color.length)];
    this.radius = 1;
  };

  pImg.functions.particles.SingleImageParticle.prototype.draw = function () {
    pImg.canvas.context.fillStyle = this.color;
    pImg.canvas.context.beginPath();
    pImg.canvas.context.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
    pImg.canvas.context.fill();
  };

  pImg.functions.particles.createImageParticles = function (pixel_data) {
    const inc = Math.round(pixel_data.width / pImg.particles.density);
    for (let i = 0; i < pixel_data.width; i += inc) {
      for (let j = 0; j < pixel_data.height; j += inc) {
        if (pixel_data.data[(i + j * pixel_data.width) * 4 + 3] > 128) {
          const dest = { x: pImg.image.x + i, y: pImg.image.y + j };
          const init = { x: Math.random() * pImg.canvas.w, y: Math.random() * pImg.canvas.h };
          pImg.particles.array.push(new pImg.functions.particles.SingleImageParticle(init, dest));
        }
      }
    }
  };

  pImg.functions.particles.animateParticles = function () {
    pImg.functions.canvas.clear();
    for (let p of pImg.particles.array) {
      p.vx += (p.dest_x - p.x) / 500;
      p.vy += (p.dest_y - p.y) / 500;
      p.vx *= p.friction;
      p.vy *= p.friction;
      p.x += p.vx;
      p.y += p.vy;
      p.draw();
    }
    requestAnimationFrame(pImg.functions.particles.animateParticles);
  };

  pImg.functions.launch = function () {
    pImg.functions.canvas.init();
    pImg.functions.image.init();
  };

  pImg.functions.launch();
};

/* ===== GLOBAL HELPERS ===== */

Object.deepExtend = function (dest, src) {
  for (let p in src) {
    if (src[p] && src[p].constructor === Object) {
      dest[p] = dest[p] || {};
      Object.deepExtend(dest[p], src[p]);
    } else dest[p] = src[p];
  }
  return dest;
};

window.particleImageDisplay = function (id) {
  const el = document.getElementById(id);
  const canvas = document.createElement("canvas");
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  el.appendChild(canvas);

  const json = el.dataset.paramsSrc;
  const xhr = new XMLHttpRequest();
  xhr.open("GET", json, false);
  xhr.send();

  const params = JSON.parse(xhr.responseText);
  new ParticleImageDisplayer(id, canvas, params);
};
